/**
 * /pressure-testing/invoices/:invoiceId — one PT invoice on its own page (PT-6,
 * fieldiq-api#939). A row on the PT invoice register, Finance → Invoices, a
 * ticket's "Invoiced on PTI-NNNN" link, or a fresh Generate opens it here. It
 * was a slide-over drawer until drawers and split panes beside a list were
 * banned (CLAUDE.md). A legacy `/pressure-testing/invoices?invoice=<id>` link
 * is forwarded here by the register.
 *
 * EVERY FIGURE IS THE API'S, AS STORED AT GENERATION. Lines, subtotal, each
 * levied tax (label, percent, amount), the tax's provenance and the total are
 * read from `GET /pressure-testing/invoices/:id` — the same loader the PDF and
 * the send use, so the page cannot disagree with what the customer receives.
 * Nothing here adds lines up or computes tax. A $0 line reads "No charge", as
 * on the PDF. A tax rate that levies 0 gets no row (Alberta has no PST).
 *
 * LINES ARE GROUPED BY SOURCE TICKET. Each group header is a full-width row on a
 * transparent rail — a ticket heading, not a record (CLAUDE.md, grouped rows).
 *
 * ACTIONS, for PT_INVOICE_WRITE (admin, office_staff); a dispatcher reads only:
 *   draft / sent / approved   Preview PDF · Send · Void
 *   paid                      Preview PDF (the API refuses send and void)
 *   void                      Preview PDF (stamped VOID). Final: nothing else.
 * Void takes a reason and returns the invoice's tickets to approved, so they
 * show again under Ready to invoice. The actions sit on the band, top right.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Ban, FileText, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api, getBlobApiErrorMessage } from '../../api/client';
import { PageHeaderBand } from '../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { usePtInvoice, usePtInvoiceRecipient, useSendPtInvoice, useVoidPtInvoice } from '../../hooks/usePressureTesting';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../lib/blob-tab';
import { formatCalendarDate, formatInstant } from '../../lib/calendar-date';
import { fmtCurrency } from '../../lib/display-labels';
import { isNotFound, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import {
  canWritePtInvoices,
  ptInvoiceStatusSkin,
  ptLineKindLabel,
  ptQtyText,
  ptRateText,
  ptTaxProvenanceText,
  ptTaxRowLabel,
  PT_SEND_BLOCKING_FALLBACK,
} from '../../lib/pressure-testing';
import type { PtInvoice, PtInvoiceLine } from '../../types/pressure-testing';
import { CcField, RecipientPreview, parseCcInput } from '../downhole/sendBits';
import { PT_BAND_SECONDARY, PT_INPUT, PT_SECONDARY, PtDialog, PtField, PtSkinBadge, PtSpec } from './PtBits';
import { FormError, ptWriteError } from './PtJobDialogs';
import { useBackToList } from '../downhole/recordPage';

const LIST = '/pressure-testing/invoices';

const day = (v: string | null | undefined) => formatCalendarDate(v) ?? '—';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The invoice's lines, in the API's order, under the ticket each came from. */
export function ptInvoiceLineGroups(inv: Pick<PtInvoice, 'lines' | 'tickets'>) {
  const groups: { ticketId: string; ticketNumber: string; lines: PtInvoiceLine[] }[] = [];
  for (const l of inv.lines) {
    let g = groups.find((x) => x.ticketId === l.ticket_id);
    if (!g) {
      g = { ticketId: l.ticket_id, ticketNumber: l.ticket_number, lines: [] };
      groups.push(g);
    }
    g.lines.push(l);
  }
  return groups;
}

/** The levied tax rows, as the PDF prints them: a stored label with a percent above 0. */
export function ptLeviedTaxRows(inv: PtInvoice) {
  return [
    { label: inv.tax_rate_1_label, percent: inv.tax_rate_1_percent, amount: inv.tax_rate_1_amount },
    { label: inv.tax_rate_2_label, percent: inv.tax_rate_2_percent, amount: inv.tax_rate_2_amount },
  ].filter((r) => !!r.label && r.percent !== null && r.percent > 0 && r.amount !== null);
}

function SendDialog({ inv, onClose }: { inv: PtInvoice; onClose: () => void }) {
  const tz = useTenantTimeZone();
  const recipient = usePtInvoiceRecipient(inv.id);
  const send = useSendPtInvoice();
  const resolved = recipient.data?.data?.to?.email ?? '';
  const [to, setTo] = useState<string | null>(null);
  const [ccText, setCcText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const toValue = to ?? resolved;
  const cc = parseCcInput(ccText);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const typed = toValue.trim();
    if (!typed) return setError('Enter an address to send this invoice to.');
    if (!EMAIL_RE.test(typed)) return setError(`"${typed}" is not a valid email address.`);
    if (cc.problem) return setError(cc.problem);
    // The resolver's own address is sent as no `to`, so the API records where it came from.
    const body = { id: inv.id, cc: cc.cc, ...(typed.toLowerCase() === resolved.toLowerCase() ? {} : { to: typed }) };
    send.mutate(body, {
      onSuccess: (res) => {
        toast.success(`${inv.invoice_number} sent to ${res.data?.sent_to ?? typed}`);
        onClose();
      },
      onError: (err) => setError(ptWriteError(err, 'The invoice was not sent.').message),
    });
  };

  return (
    <PtDialog
      open
      title={`Send ${inv.invoice_number}`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="pt-send-invoice"
            disabled={send.isPending}
            data-testid="pt-invoice-send-confirm"
            className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
          >
            {send.isPending ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <Send className="size-4" aria-hidden="true" />}
            Send with PDF
          </button>
        </>
      }
    >
      <form id="pt-send-invoice" onSubmit={submit} className="space-y-4" noValidate>
        <RecipientPreview query={recipient} tz={tz} subject="who this invoice goes to" />
        <PtField label="To" hint="The address above, unless you type another for this send.">
          <input
            className={PT_INPUT}
            type="email"
            inputMode="email"
            value={toValue}
            onChange={(e) => setTo(e.target.value)}
            maxLength={255}
            data-testid="pt-invoice-send-to"
          />
        </PtField>
        <CcField value={ccText} onChange={setCcText} problem={ccText.trim() ? cc.problem : null} />
        <FormError message={error} />
      </form>
    </PtDialog>
  );
}

function VoidDialog({ inv, onClose }: { inv: PtInvoice; onClose: () => void }) {
  const voidInvoice = useVoidPtInvoice();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const count = inv.tickets.length;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!reason.trim()) return setError('A reason is required to void an invoice.');
    voidInvoice.mutate(
      { id: inv.id, reason: reason.trim() },
      {
        onSuccess: () => {
          toast.success(`${inv.invoice_number} voided`);
          onClose();
        },
        onError: (err) => setError(ptWriteError(err, 'The invoice was not voided.').message),
      },
    );
  };

  return (
    <PtDialog
      open
      title={`Void ${inv.invoice_number}?`}
      onClose={onClose}
      footer={
        <>
          <button type="button" className={PT_SECONDARY} onClick={onClose}>
            Keep invoice
          </button>
          <button
            type="submit"
            form="pt-void-invoice"
            disabled={voidInvoice.isPending}
            data-testid="pt-invoice-void-confirm"
            className="inline-flex h-9 items-center gap-1.5 rounded-fi-control border border-black/20 bg-fi-signal-red px-3 text-sm font-bold text-fi-signal-ink disabled:cursor-not-allowed disabled:opacity-60"
          >
            {voidInvoice.isPending && <Loader2 className="size-4 animate-spin" aria-hidden="true" />}
            Void invoice
          </button>
        </>
      }
    >
      <form id="pt-void-invoice" onSubmit={submit} className="space-y-4" noValidate>
        <p className="text-sm text-fieldiq-text">
          A void invoice is kept for the record and cannot be sent, paid or reopened. Its {count === 1 ? 'field ticket goes' : `${count} field tickets go`} back to
          approved, ready to invoice again.
        </p>
        <FormError message={error} />
        <PtField label="Reason">
          <textarea
            className={`${PT_INPUT} h-24 py-2`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            maxLength={1000}
            data-testid="pt-invoice-void-reason"
            autoFocus
          />
        </PtField>
      </form>
    </PtDialog>
  );
}

export default function InvoiceDetailScreen() {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const back = useBackToList(LIST);
  const { user } = useAuth();
  const tz = useTenantTimeZone();
  const query = usePtInvoice(invoiceId);
  const inv = query.data?.data ?? null;
  const failure = queryLoadFailure(query);
  // A 404 is an answer about the id, not about the network.
  const notFound = !inv && !query.isLoading && (isNotFound(queryFailureError(query)) || !failure);
  const [dialog, setDialog] = useState<'send' | 'void' | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const canWrite = canWritePtInvoices(user?.role);
  const status = inv?.status ?? null;
  const live = status !== null && status !== 'void' && status !== 'paid';
  const sendBlocked = !!inv && PT_SEND_BLOCKING_FALLBACK.includes(inv.tax_fallback_reason ?? '') && !inv.tax_exempt;

  const openPdf = async () => {
    if (!inv) return;
    setActionError(null);
    // Claimed synchronously, inside the click's activation window (lib/blob-tab.ts).
    const win = claimBlobTab();
    if (!win) {
      setActionError(POPUP_BLOCKED_MESSAGE);
      return;
    }
    setPdfBusy(true);
    try {
      const res = await api.pressureTesting.invoices.pdf(inv.id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as BlobPart], { type: 'application/pdf' });
      showBlobInTab(win, blob);
    } catch (err) {
      win.close();
      setActionError(await getBlobApiErrorMessage(err, 'The PDF could not be loaded.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const cur = inv?.currency ?? null;
  const taxRows = inv ? ptLeviedTaxRows(inv) : [];
  const signerOf = (ticketId: string) => inv?.tickets.find((t) => t.id === ticketId);

  const actions = inv ? (
    <div className="flex flex-wrap gap-2" data-testid="pt-invoice-actions">
      <button type="button" className={PT_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy} data-testid="pt-invoice-pdf">
        {pdfBusy ? <Loader2 className="size-4 animate-spin" aria-hidden="true" /> : <FileText className="size-4" aria-hidden="true" />}
        Preview PDF
      </button>
      {canWrite && live && (
        <button type="button" className={PT_BAND_SECONDARY} onClick={() => setDialog('void')} data-testid="pt-invoice-void">
          <Ban className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {canWrite && live && (
        <button
          type="button"
          className="fi-action fi-action--standard fi-action--primary disabled:cursor-not-allowed disabled:opacity-50"
          onClick={() => setDialog('send')}
          disabled={sendBlocked}
          data-testid="pt-invoice-send"
        >
          <Send className="size-4" aria-hidden="true" />
          {inv.sent_at ? 'Send again' : 'Send'}
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="pt-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Invoice"
        title={inv ? inv.invoice_number || 'Invoice' : query.isLoading ? 'Loading…' : 'Invoice'}
        onBack={back}
        backLabel="Back to invoices"
        actions={actions}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="pt-invoice-detail">
          {!inv && query.isLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading the invoice" />
            </div>
          ) : notFound ? (
            <p className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-6 text-sm text-fieldiq-text" data-testid="pt-invoice-not-found">
              This invoice was not found.
            </p>
          ) : !inv ? (
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? undefined} subject="this invoice" />
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                <PtSkinBadge skin={ptInvoiceStatusSkin(status)} testId="pt-invoice-status" value={status} />
                {inv.job_id ? (
                  <Link to={`/pressure-testing/jobs/${inv.job_id}`} className="text-sm font-bold text-fieldiq-text underline">
                    {inv.job_number || '—'}
                  </Link>
                ) : (
                  <span className="text-sm text-fieldiq-muted">Several jobs</span>
                )}
                <span className="text-sm text-fieldiq-muted">{inv.customer_name || '—'}</span>
              </div>
              {/* A failed BACKGROUND refetch keeps the invoice on screen and says so. */}
              {failure && (
                <p role="status" className="text-xs text-fieldiq-muted">
                  This invoice could not be refreshed just now, so it shows what loaded earlier.
                </p>
              )}
              {status === 'void' && (
                <div role="status" data-testid="pt-invoice-void-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">Void {formatInstant(inv.voided_at, tz) ?? ''}. This invoice is final and read-only.</p>
                  <p className="mt-1 whitespace-pre-wrap">{inv.void_reason || '—'}</p>
                </div>
              )}
              {sendBlocked && status !== 'void' && (
                <div role="note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">
                    Tax for {inv.jurisdiction || 'this province or state'}{' '}
                    {inv.tax_fallback_reason === 'not_yet_effective' ? 'is not in effect yet on the invoice date' : 'is not set up'}, so the company&rsquo;s default
                    rate was used and this invoice cannot be sent.
                  </p>
                  <p className="mt-1">Add the rate in Settings → Regional &amp; Tax, then void this invoice and generate it again.</p>
                </div>
              )}
              {actionError && (
                <p role="alert" data-testid="pt-invoice-action-error" className="rounded-fi-panel border border-fi-signal-red/40 border-l-4 border-l-fi-red bg-fi-signal-red/10 px-4 py-3 text-sm text-fieldiq-text">
                  {actionError}
                </p>
              )}

              <section className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-3">
                  <PtSpec label="Invoice date" value={day(inv.invoice_date)} />
                  <PtSpec label="Due date" value={day(inv.due_date)} />
                  <PtSpec label="Currency" value={inv.currency || '—'} />
                  <PtSpec label="PO" value={inv.po_number || '—'} mono />
                  <PtSpec label="AFE" value={inv.afe_number || '—'} mono />
                  <PtSpec label="Cost code" value={inv.cost_code || '—'} mono />
                  <PtSpec label="Location" value={[inv.location_name, inv.lsd].filter(Boolean).join(' · ') || '—'} />
                  <PtSpec
                    label="Last sent"
                    value={inv.sent_at ? `${formatInstant(inv.sent_at, tz) ?? '—'} to ${inv.sent_to || '—'}` : 'Not sent'}
                  />
                  <PtSpec label="Tickets" value={inv.tickets.map((t) => t.ticket_number).join(', ') || '—'} mono />
                </dl>
                {inv.notes && <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text whitespace-pre-wrap">{inv.notes}</p>}
              </section>

              <section className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card" data-testid="pt-invoice-lines">
                <div className="border-b border-fieldiq-border px-4 py-3">
                  <p className="text-[0.62rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Charges</p>
                  <h3 className="text-base font-extrabold text-white">Lines</h3>
                </div>
                {inv.lines.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-fieldiq-text">This invoice has no lines.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">Charge</th>
                          <th scope="col" className="px-4 py-3 text-right">Qty</th>
                          <th scope="col" className="px-4 py-3 text-right">Rate</th>
                          <th scope="col" className="px-4 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      {ptInvoiceLineGroups(inv).map((g) => {
                        const t = signerOf(g.ticketId);
                        return (
                          <tbody key={g.ticketId} data-testid="pt-invoice-line-group">
                            <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/30">
                              <th scope="colgroup" colSpan={4} className="px-4 py-2 text-left text-xs font-bold text-fieldiq-text">
                                <span className="font-mono">{g.ticketNumber}</span>
                                {t?.work_date ? ` · ${day(t.work_date)}` : ''}
                                {` · Signed by ${t?.signer_name || '—'}`}
                              </th>
                            </tr>
                            {g.lines.map((l) => (
                              <tr key={l.id} data-testid="pt-invoice-line" className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                                <td className="px-4 py-3">
                                  <p className="text-sm font-semibold text-white">{l.description}</p>
                                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                                    {ptLineKindLabel(l.kind)}
                                    {l.work_date ? ` · ${day(l.work_date)}` : ''}
                                  </p>
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{ptQtyText(l.quantity, l.unit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{ptRateText(l.rate, cur, l.unit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-white">
                                  {l.amount === 0 ? 'No charge' : fmtCurrency(l.amount, cur)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        );
                      })}
                      <tfoot>
                        <tr className="border-t-2 border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-2 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                            Subtotal
                          </th>
                          <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white" data-testid="pt-invoice-subtotal">
                            {fmtCurrency(inv.subtotal, cur)}
                          </td>
                        </tr>
                        {taxRows.length > 0 ? (
                          taxRows.map((r, i) => (
                            <tr key={i} className="border-l-4 border-l-transparent" data-testid="pt-invoice-tax-row">
                              <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">
                                {ptTaxRowLabel(r.label, r.percent)}
                              </th>
                              <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(r.amount, cur)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr className="border-l-4 border-l-transparent" data-testid="pt-invoice-tax-row">
                            <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">
                              Tax
                            </th>
                            <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(inv.tax_amount, cur)}</td>
                          </tr>
                        )}
                        <tr className="border-l-4 border-l-transparent">
                          <td colSpan={4} className="px-4 pb-2 text-right text-xs text-fieldiq-muted" data-testid="pt-invoice-tax-source">
                            Tax: {ptTaxProvenanceText(inv)}
                          </td>
                        </tr>
                        <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-3 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                            Total ({inv.currency || '—'})
                          </th>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-base font-extrabold tabular-nums text-white" data-testid="pt-invoice-total">
                            {fmtCurrency(inv.total, cur)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </section>

      {inv && dialog === 'send' && <SendDialog inv={inv} onClose={() => setDialog(null)} />}
      {inv && dialog === 'void' && <VoidDialog inv={inv} onClose={() => setDialog(null)} />}
    </div>
  );
}
